import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Pin, Lock, MessageCircle, Eye, Plus, ChevronRight, ChevronLeft, MessagesSquare } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import UserBadge from "@/components/UserBadge";
import Breadcrumbs from "@/components/Breadcrumbs";
import PrefixBadge from "@/components/PrefixBadge";
import { useAuth } from "@/contexts/AuthContext";
import { t } from "@/lib/i18n";
import { timeAgo } from "@/lib/format";
import { toast } from "sonner";

export default function ForumPage() {
  const { forumId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    setLoading(true);
    setPage(1);
  }, [forumId]);

  useEffect(() => {
    setLoading(true);
    api.get(`/forums/${forumId}?page=${page}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [forumId, page]);

  const onNewThread = () => {
    if (!user) {
      toast.error("لاگ اِن کریں");
      nav("/login");
      return;
    }
    nav(`/f/${forumId}/new`);
  };

  const crumbs = data
    ? [
        { label: t.home, to: "/" },
        { label: data.category?.name, to: "/" },
        ...(data.parent_forum ? [{ label: data.parent_forum.name, to: `/f/${data.parent_forum.id}` }] : []),
        { label: data.forum.name },
      ]
    : [];

  return (
    <div className="space-y-6" data-testid="forum-page">
      {data && (
        <>
          <Breadcrumbs items={crumbs} />
          <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-primary pb-4">
            <div>
              <h1 className="urdu-naskh text-3xl text-primary font-semibold leading-tight">{data.forum.name}</h1>
              <p className="urdu-naskh text-muted-foreground mt-1">{data.forum.description}</p>
            </div>
            <Button onClick={onNewThread} size="lg" className="bg-primary hover:bg-primary/90 font-eng gap-2" data-testid="new-thread-btn">
              <Plus size={16} /> <span className="urdu-naskh">{t.newThread}</span>
            </Button>
          </div>

          {data.subforums?.length > 0 && (
            <div data-testid="subforums-section">
              <h2 className="urdu-naskh text-base font-semibold text-primary/80 mb-2">{t.subForums}</h2>
              <Card className="divide-y divide-border overflow-hidden">
                {data.subforums.map((sf) => (
                  <Link
                    to={`/f/${sf.id}`}
                    key={sf.id}
                    className="forum-row block px-4 py-3 bg-card group"
                    data-testid={`sub-forum-row-${sf.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
                        <MessagesSquare size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="urdu-naskh font-semibold text-primary group-hover:underline underline-offset-4">
                          {sf.name}
                        </h3>
                        <p className="urdu-naskh text-xs text-muted-foreground">{sf.description}</p>
                      </div>
                      <div className="hidden sm:block text-xs text-muted-foreground font-eng shrink-0">
                        <span className="font-semibold text-foreground">{sf.thread_count}</span> {t.threads} · <span className="font-semibold text-foreground">{sf.post_count}</span> {t.posts}
                      </div>
                    </div>
                  </Link>
                ))}
              </Card>
            </div>
          )}
        </>
      )}

      {loading && (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {data && (
        <div>
          <h2 className="urdu-naskh text-base font-semibold text-primary/80 mb-2">{t.threads}</h2>
          <Card className="divide-y divide-border overflow-hidden" data-testid="thread-list">
            {data.threads.length === 0 && (
              <div className="p-8 text-center urdu-naskh text-muted-foreground">{t.noThreads}</div>
            )}
            {data.threads.map((th) => (
              <Link
                to={`/t/${th.id}`}
                key={th.id}
                className="forum-row block px-4 sm:px-5 py-4 bg-card group"
                data-testid={`thread-row-${th.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 mt-1">
                    {th.is_pinned ? (
                      <Pin size={18} className="text-accent" />
                    ) : th.is_locked ? (
                      <Lock size={18} className="text-muted-foreground" />
                    ) : (
                      <MessageCircle size={18} className="text-primary/60" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {th.prefix && <PrefixBadge prefix={th.prefix} />}
                      {th.is_pinned && <Badge className="bg-accent text-accent-foreground text-[10px] font-eng">{t.pinned}</Badge>}
                      {th.is_locked && <Badge variant="secondary" className="text-[10px] font-eng">{t.locked}</Badge>}
                      <h3 className="urdu-naskh text-lg font-semibold text-foreground group-hover:text-primary group-hover:underline underline-offset-4">
                        {th.title}
                      </h3>
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground font-eng">
                      <span className="urdu-naskh">{t.by}</span>
                      <UserBadge user={th.author} size="sm" showRole={false} />
                      <span>·</span>
                      <span>{timeAgo(th.created_at)}</span>
                    </div>
                  </div>
                  <div className="hidden sm:flex flex-col items-end text-xs text-muted-foreground font-eng gap-0.5 shrink-0">
                    <div className="flex items-center gap-1"><MessageCircle size={12} /> <span className="font-semibold text-foreground">{th.reply_count}</span></div>
                    <div className="flex items-center gap-1"><Eye size={12} /> <span>{th.view_count}</span></div>
                  </div>
                  <div className="hidden md:block w-44 shrink-0 border-r border-border pr-4 text-xs">
                    <div className="text-muted-foreground font-eng">{timeAgo(th.last_post_at)}</div>
                    <div className="urdu-naskh text-primary">{th.last_post_user?.username}</div>
                  </div>
                </div>
              </Link>
            ))}
          </Card>
        </div>
      )}

      {data && data.pagination.pages > 1 && (
        <Pagination page={page} pages={data.pagination.pages} onChange={setPage} />
      )}
    </div>
  );
}

export function Pagination({ page, pages, onChange }) {
  return (
    <div className="flex items-center justify-center gap-2 font-eng text-sm" dir="ltr" data-testid="pagination">
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)} data-testid="page-prev">
        <ChevronLeft size={14} />
      </Button>
      <span className="px-2">
        Page <b>{page}</b> of <b>{pages}</b>
      </span>
      <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)} data-testid="page-next">
        <ChevronRight size={14} />
      </Button>
    </div>
  );
}
